'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Star, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { useRouter } from 'next/navigation';

// 类型定义
interface FixedAnnouncementBannerProps {
  message?: string;
  disclaimer?: string;
  ctaText?: string;
  storageKey?: string;
  variant?: 'green' | 'gradient';
}

interface VariantStyles {
  background: string;
  border: string;
  iconBg: string;
  textColor: string;
  disclaimerColor: string;
  buttonStyles: string;
  closeColor: string;
  glow: string;
}

// 优化的图标组件 - 使用React.memo减少重渲染
const BannerIcon: React.FC<{ variant: string; isMobile?: boolean }> = React.memo(({ variant, isMobile = false }) => {
  const iconSize = isMobile ? "h-3 w-3" : "h-4 w-4";

  switch (variant) {
    case 'gradient':
      return <Sparkles className={`${iconSize} text-emerald-600`} />;
    case 'green':
      return <Zap className={`${iconSize} text-emerald-600`} />;
    default:
      return <Star className={`${iconSize} text-emerald-600 fill-current`} />;
  }
});

BannerIcon.displayName = 'BannerIcon';

// 优化的关闭按钮组件
const CloseButton: React.FC<{
  onClick: () => void;
  closeColor: string;
  isMobile?: boolean;
}> = React.memo(({ onClick, closeColor, isMobile = false }) => (
  <button
    onClick={onClick}
    className={`${closeColor} transition-all duration-300 hover:bg-white/20 rounded-lg hover:scale-110 transform ${isMobile ? 'p-1' : 'p-2'}`}
    aria-label="Close announcement banner"
    title="Close announcement banner"
  >
    <X className={isMobile ? "h-4 w-4" : "h-5 w-5"} aria-hidden="true" />
  </button>
));

CloseButton.displayName = 'CloseButton';

// 优化的CTA按钮组件
const CTAButton: React.FC<{
  onClick: () => void;
  buttonStyles: string;
  ctaText: string;
  isMobile?: boolean;
}> = React.memo(({ onClick, buttonStyles, ctaText, isMobile = false }) => (
  <button
    onClick={onClick}
    className={`group flex items-center space-x-1 ${buttonStyles} font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg transform ${isMobile
        ? 'px-2 py-1 rounded-md text-xs'
        : 'px-4 py-2 rounded-xl text-sm'
      }`}
  >
    <span className="whitespace-nowrap">{ctaText}</span>
    <ArrowRight
      className={`group-hover:translate-x-1 transition-transform duration-300 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`}
    />
  </button>
));

CTAButton.displayName = 'CTAButton';

// 主组件
const FixedAnnouncementBanner: React.FC<FixedAnnouncementBannerProps> = ({
  message = "veo3 is now available on wan22.io",
  disclaimer = "Not affiliated with Google",
  ctaText = "Try Now",
  storageKey = "announcement-banner-dismissed",
  variant = "green"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 使用useMemo缓存样式配置，避免重复计算
  const styles = useMemo((): VariantStyles => {
    const baseStyles = {
      green: {
        background: 'bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500',
        border: 'border-none',
        iconBg: 'bg-white',
        textColor: 'text-white',
        disclaimerColor: 'text-green-100/60',
        buttonStyles: 'bg-white text-green-700 hover:bg-green-50 hover:text-green-800 border border-white/30 shadow-md',
        closeColor: 'text-white/80 hover:text-white',
        glow: 'shadow-[0_0_30px_rgba(34,197,94,0.3)]'
      },
      gradient: {
        background: 'bg-gradient-to-r from-violet-600 via-green-600 to-fuchsia-600',
        border: 'border-violet-400/50',
        iconBg: 'bg-white',
        textColor: 'text-white',
        disclaimerColor: 'text-violet-100/30',
        buttonStyles: 'bg-white text-green-800 hover:bg-white hover:text-green-900 border border-white/30 shadow-lg',
        closeColor: 'text-violet-100/70 hover:text-white',
        glow: 'shadow-[0_0_25px_rgba(139,92,246,0.4)]'
      }
    };

    return baseStyles[variant] || baseStyles.green;
  }, [variant]);

  // 使用useCallback优化事件处理函数
  const handleTryNow = useCallback(() => {
    // 使用CSS选择器查找目标元素，减少DOM查询
    const veo3Section = document.querySelector('[data-veo3-generator], .notranslate, #veo3-generator');

    if (veo3Section) {
      veo3Section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // 使用Next.js router进行客户端导航
      if (window.location.pathname !== '/veo3') {
        router.push('/veo3');
      } else {
        // 如果已在veo3页面，滚动到顶部
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  }, [router]);

  const handleClose = useCallback(() => {
    // 先添加关闭动画类
    const bannerElement = document.querySelector('.announcement-banner-fixed');
    if (bannerElement) {
      bannerElement.classList.add('closing');
    }

    // 延迟隐藏，让动画完成
    setTimeout(() => {
      setIsVisible(false);
      // 使用try-catch避免localStorage错误
      try {
        localStorage.setItem(storageKey, 'true');
        // 触发自定义事件，通知HeaderSpacer更新
        window.dispatchEvent(new CustomEvent('banner-closed'));
      } catch (error) {
        console.warn('Failed to save banner state:', error);
      }
    }, 300); // 动画持续时间
  }, [storageKey]);

  // 初始化逻辑 - 使用useEffect避免SSR问题
  useEffect(() => {
    setIsMounted(true);

    // 延迟检查localStorage，避免水合问题
    const timer = setTimeout(() => {
      try {
        const isDismissed = localStorage.getItem(storageKey);
        // 默认显示banner，除非明确被关闭
        setIsVisible(isDismissed !== 'true');

        // 预加载veo3页面以提高跳转速度
        if (isDismissed !== 'true') {
          router.prefetch('/veo3');
          // 触发横幅显示事件
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('banner-opened'));
          }, 100);
        }
      } catch (error) {
        console.warn('Failed to check banner state:', error);
        setIsVisible(true); // 默认显示
      }
    }, 100); // 延迟100ms，确保客户端完全水合

    return () => clearTimeout(timer);
  }, [storageKey, router]);

  // 如果组件未挂载或不可见，返回null
  if (!isMounted || !isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed left-0 right-0 z-40 ${styles.background} ${styles.border} ${styles.glow} announcement-banner-fixed transition-all duration-500 ease-in-out`}
      style={{
        // 固定定位在header下方，使用CSS变量确保高度一致性
        top: 'var(--banner-top, var(--header-height, 4rem))',
        height: isMobile ? '2.5rem' : '3rem',
        display: 'flex',
        alignItems: 'center',
        padding: '0',
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        opacity: isVisible ? 1 : 0,
        borderTop: 'none',
        borderBottom: 'none',
        marginTop: '0'
      } as React.CSSProperties}
    >
      <div className="w-full px-4 sm:px-8 py-1 sm:py-2">
        {/* 桌面端布局 */}
        <div className="hidden sm:flex items-center justify-between">
          {/* Left side - Icon and text */}
          <div className="flex items-center space-x-4 min-w-0 flex-1">
            <div className="flex-shrink-0">
              <div className={`flex items-center justify-center w-7 h-7 ${styles.iconBg} rounded-full shadow-md`}>
                <BannerIcon variant={variant} isMobile={false} />
              </div>
            </div>
            <div className="flex items-center space-x-3 min-w-0">
              <span className={`${styles.textColor} font-bold text-base leading-tight`}>
                {message}
              </span>
              <span className={`${styles.disclaimerColor} text-xs whitespace-nowrap font-medium`}>
                {disclaimer}
              </span>
            </div>
          </div>

          {/* Right side - CTA and close button */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <CTAButton
              onClick={handleTryNow}
              buttonStyles={styles.buttonStyles}
              ctaText={ctaText}
            />
            <CloseButton
              onClick={handleClose}
              closeColor={styles.closeColor}
            />
          </div>
        </div>

        {/* 移动端布局 */}
        <div className="grid sm:hidden grid-cols-[1fr_auto] gap-1 items-center">
          {/* Left side - Icon and text */}
          <div className="flex items-center space-x-1.5 min-w-0 pr-0.5">
            <div className="flex-shrink-0">
              <div className={`flex items-center justify-center w-5 h-5 ${styles.iconBg} rounded-full shadow-sm`}>
                <BannerIcon variant={variant} isMobile={true} />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className={`${styles.textColor} font-bold text-xs leading-tight mb-0.5`}>
                {message}
              </div>
              <div className={`${styles.disclaimerColor} text-[9px] leading-none font-medium`}>
                {disclaimer}
              </div>
            </div>
          </div>

          {/* Right side - CTA and close button */}
          <div className="flex items-center space-x-0.5 flex-shrink-0">
            <CTAButton
              onClick={handleTryNow}
              buttonStyles={styles.buttonStyles}
              ctaText={ctaText}
              isMobile
            />
            <CloseButton
              onClick={handleClose}
              closeColor={styles.closeColor}
              isMobile
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixedAnnouncementBanner; 